import React from "react";
import {
    Dialog, DialogContent, TextField, InputAdornment, Box, Typography,
    List, ListItemButton, ListItemIcon, ListItemText,
} from "@mui/material";
import {SearchOutlined, NorthWest} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {Blue3, Grey5, Grey7} from "../../theme/styles";

type Props = {
    open: boolean;
    onClose: () => void;
};

// The catalog destinations Global Search can jump to. (Client-side for now;
// swap for a backend-backed result set when search is wired up.)
const SEARCHABLE: {label: string; route: string}[] = [
    {label: "Home", route: "/"},
    {label: "Report builder", route: "/reporting"},
];

/**
 * Centered global-search popup: a search field with results rendered below it.
 * Backdrop click (anywhere outside the popup) and Escape both close it — the
 * default MUI Dialog behaviour via onClose.
 */
export default function GlobalSearch({open, onClose}: Props) {
    const navigate = useNavigate();
    const [query, setQuery] = React.useState<string>("");

    // Start each open with an empty field.
    React.useEffect(() => {
        if (open) setQuery("");
    }, [open]);

    const q = query.trim().toLowerCase();
    const results = q ? SEARCHABLE.filter((s) => s.label.toLowerCase().includes(q)) : [];

    const go = (route: string) => {
        onClose();
        navigate(route);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{paper: {sx: {borderRadius: 2}}}}>
            <DialogContent sx={{p: 2}}>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined sx={{color: Blue3}}/>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {/* Results area — appears below the search field. */}
                <Box sx={{mt: 2, minHeight: 220, maxHeight: 380, overflowY: "auto"}}>
                    {!q ? (
                        <Typography sx={{color: Grey5, fontSize: 14, p: 1}}>
                            Type to search across the catalog…
                        </Typography>
                    ) : results.length === 0 ? (
                        <Typography sx={{color: Grey5, fontSize: 14, p: 1}}>
                            No results for “{query}”.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {results.map((r) => (
                                <ListItemButton key={r.route} onClick={() => go(r.route)} sx={{borderRadius: 1}}>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <NorthWest sx={{color: Grey5, fontSize: 18}}/>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={r.label}
                                        slotProps={{primary: {sx: {color: Grey7, fontSize: 14}}}}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
